export interface Template {
  id: string;
  source: string;
  createdAt: number;
  updatedAt: number;
}

// ── Content nodes ─────────────────────────────────────────────────────────────

export type ContentNode = SectionNode | HeadingNode | TextNode | FieldNode | ScoreGroupNode;

export interface SectionNode {
  type: "section";
  id: string; // slug of title, deduplicated
  title: string;
  required: boolean;
}

export interface HeadingNode {
  type: "heading";
  text: string;
  level: number;
}

export interface TextNode {
  type: "text";
  markdown: string; // raw markdown (bold, italic, etc.)
}

export interface FieldNode {
  type: "field";
  field: ContentField;
}

export interface ScoreGroupNode {
  type: "scoregroup";
  id: string; // name used in {id:total}, {id:min}, {id:max}, {id:avg} refs
}

// ── Template content ──────────────────────────────────────────────────────────

export interface TemplateContent {
  name: string;
  isSubTemplate: boolean;
  category?: string;
  variables: string[];
  content: ContentNode[];
}

// ── Fields ────────────────────────────────────────────────────────────────────

import { z } from "zod";

const BaseFieldSchema = z.object({ id: z.string(), label: z.string() });

export const TextFieldSchema = BaseFieldSchema.extend({
  defaultValue: z.string(),
  multiline: z.boolean(),
  optional: z.boolean(),
  type: z.literal("text"),
});

export type TextField = z.infer<typeof TextFieldSchema>;

export const SelectFieldSchema = BaseFieldSchema.extend({
  options: z.array(z.string()),
  type: z.literal("select"),
});

export type SelectField = z.infer<typeof SelectFieldSchema>;

export const CheckboxListFieldSchema = BaseFieldSchema.extend({
  defaultAll: z.boolean().optional(),
  items: z.array(z.string()),
  type: z.literal("checkbox-list"),
});

export type CheckboxListField = z.infer<typeof CheckboxListFieldSchema>;

export const StatementListFieldSchema = BaseFieldSchema.extend({
  items: z.array(z.string()),
  type: z.literal("statement-list"),
});

export type StatementListField = z.infer<typeof StatementListFieldSchema>;

export const ScoreOptionSchema = z.object({ label: z.string(), value: z.number() });

export type ScoreOption = z.infer<typeof ScoreOptionSchema>;

export const ScoreFieldSchema = BaseFieldSchema.extend({
  max: z.number(),
  min: z.number().optional(), // minimum value (default 0)
  pairAgg: z.enum(["min", "max"]).optional(), // how to aggregate the bilateral pair
  pairId: z.string().optional(), // bilateral pair group name (field-level)
  scoregroupId: z.string().optional(), // which @scoregroup this field belongs to
  scoreOptions: z.array(ScoreOptionSchema).optional(),
  type: z.literal("score"),
});

export type ScoreField = z.infer<typeof ScoreFieldSchema>;

export const GridRowSchema = z.object({
  defaults: z.record(z.string(), z.string()).optional(),
  label: z.string(),
});

export const GridFieldSchema = BaseFieldSchema.extend({
  columns: z.array(z.string()),
  rows: z.array(GridRowSchema),
  type: z.literal("grid"),
});

export type GridField = z.infer<typeof GridFieldSchema>;

export const SubTemplateFieldSchema = BaseFieldSchema.extend({
  templateId: z.string(),
  type: z.literal("sub-template"),
});

export type SubTemplateField = z.infer<typeof SubTemplateFieldSchema>;

export const DateFieldSchema = BaseFieldSchema.extend({
  defaultValue: z.string(), // ISO date string "YYYY-MM-DD" or ""
  optional: z.boolean(),
  type: z.literal("date"),
});

export type DateField = z.infer<typeof DateFieldSchema>;

export const ContentFieldSchema = z.discriminatedUnion("type", [
  TextFieldSchema,
  SelectFieldSchema,
  CheckboxListFieldSchema,
  StatementListFieldSchema,
  ScoreFieldSchema,
  GridFieldSchema,
  SubTemplateFieldSchema,
  DateFieldSchema,
]);

export type ContentField = z.infer<typeof ContentFieldSchema>;
